import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Download, Upload, Users, Database, ShieldCheck, BarChart3, Activity, AlertCircle, Check, Settings,
  Mail, Send, ChevronDown, Clock, FileText, Loader2, RefreshCw, Lock, Unlock, KeyRound, LogIn
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import { TimeSelect, formatTime12 } from "@/components/TimeSelect";
import { AddUserDialog, RoleSelect, DeleteUserButton } from "@/components/AdminUserControls";
import PendingAccessRequests from "@/components/PendingAccessRequests";
import EmailUserDialog from "@/components/EmailUserDialog";
import AdminAuditLog from "@/components/AdminAuditLog";
import DataCleanupPanel from "@/components/DataCleanupPanel";
import StockUniversePanel from "@/components/StockUniversePanel";
import { usePersistedOpen } from "@/lib/usePersistedOpen";

const RESTORE_LABELS = {
  users_replaced: "Users", portfolios_replaced: "Portfolio holdings", watchlists_replaced: "Watchlist items",
  trades_replaced: "Trades", woodrow_accounts_replaced: "Woodrow accounts", woodrow_trades_replaced: "Woodrow trades",
  snapshots_replaced: "Woodrow snapshots", woodrow_snapshots_replaced: "Woodrow snapshots", analytics_pageviews_replaced: "Page views", settings_replaced: "Settings", analytics_replaced: "Page views",
  access_logs_replaced: "Access logs", communications_log_replaced: "Email log", communications_history_replaced: "Email history", admin_audit_log_replaced: "Activity log",
};

const RestoreResult = ({ result, onDismiss }) => {
  if (!result) return null;
  const counts = Object.entries(result.results || {}).filter(([k]) => RESTORE_LABELS[k]);
  return (
    <div className={`mt-3 border px-3 py-3 text-xs ${result.ok ? "border-bt-positive/40 bg-bt-positive/5" : "border-bt-negative/40 bg-bt-negative/5"}`} data-testid="restore-result">
      <div className="flex items-start justify-between gap-2">
        <p className={`font-mono font-semibold ${result.ok ? "text-bt-positive" : "text-bt-negative"}`} data-testid="restore-result-title">
          {result.ok ? "Restore complete" : "Restore failed"} · {result.file}
        </p>
        <button onClick={onDismiss} className="text-bt-text-tertiary hover:text-bt-text-primary font-mono" data-testid="restore-result-dismiss">✕</button>
      </div>
      {result.error && <p className="mt-1 text-bt-text-secondary">{result.error}</p>}
      {result.duplicate_ignored && (
        <p className="mt-1 text-bt-text-secondary" data-testid="restore-result-duplicate">
          This same file was already restored moments ago, so it wasn't applied twice. Counts are from that restore.
        </p>
      )}
      {counts.length > 0 && (
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono" data-testid="restore-result-counts">
          {counts.map(([k, v]) => (
            <p key={k} className="flex justify-between"><span className="text-bt-text-tertiary">{RESTORE_LABELS[k]}</span><span className="text-bt-text-primary">{Number(v).toLocaleString()}</span></p>
          ))}
        </div>
      )}
      {(result.warnings || []).length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-bt-border-subtle pt-2" data-testid="restore-result-warnings">
          {result.warnings.map((w, i) => <li key={i} className="text-bt-gold">⚠ {w}</li>)}
        </ul>
      )}
    </div>
  );
};

const utcToEt = (h, m) => {
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = (path) => axios.get(`${BACKEND}/api${path}`, { withCredentials: true });
const apiGet = (path) => axios.get(`${BACKEND}/api${path}`, { withCredentials: true });
const apiPost = (path, data) => axios.post(`${BACKEND}/api${path}`, data, { withCredentials: true });
const apiPut = (path, data) => axios.put(`${BACKEND}/api${path}`, data, { withCredentials: true });

// UTC ↔ local time helpers (preserve UTC storage on backend, display local in UI)
function utcToLocal(utcH, utcM) {
  const d = new Date();
  d.setUTCHours(utcH, utcM, 0, 0);
  return { hour: d.getHours(), minute: d.getMinutes() };
}
function localToUtc(localH, localM) {
  const d = new Date();
  d.setHours(localH, localM, 0, 0);
  return { hour: d.getUTCHours(), minute: d.getUTCMinutes() };
}
function getLocalTzLabel() {
  // e.g. "CDT" / "EST" / "GMT+5:30"
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(new Date());
  const tz = parts.find(p => p.type === "timeZoneName")?.value || "Local";
  return tz;
}

function CollapsibleHeader({ title, subtitle, icon: Icon, open, onToggle, accent = "text-bt-text-primary", testid }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-6 py-4 hover:bg-bt-surface-2/40 transition-colors text-left"
      data-testid={testid}
    >
      <div className="flex items-center gap-3 min-w-0">
        {Icon && <Icon className={`w-5 h-5 shrink-0 ${accent}`} strokeWidth={1.5} />}
        <div className="min-w-0">
          <h2 className={`font-heading text-xl font-medium leading-tight ${accent}`}>{title}</h2>
          {subtitle && <p className="text-xs text-bt-text-tertiary font-body mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
      <ChevronDown className={`w-5 h-5 text-bt-text-tertiary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

const LOG_FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "welcome", label: "Welcome", match: e => e.source === "welcome" },
  { id: "daily", label: "Daily", match: e => e.source === "daily_auto" || /daily|weekly/i.test(e.subject || "") },
  { id: "access", label: "Access", match: e => (e.source || "").startsWith("access_") },
  { id: "failed", label: "Failed", match: e => e.status && e.status !== "sent" },
];

const SOURCE_LABELS = {
  manual: "Manual", daily_auto: "Auto", welcome: "Welcome", reminder: "Reminder", winback: "Win-back",
  access_request: "Access request", access_denied: "Access denied", drawdown_alert: "Drawdown alert", limit_order_fill: "Order fill",
  backup_auto_startup: "Backup", backup_auto_weekly: "Backup",
};

export default function AdminPage() {
  const { user, checkAuth, startEmulation } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [copyright, setCopyright] = useState("");
  const [version, setVersion] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  // SMTP
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "Rockafeller Jones", use_tls: true });
  const [smtpOpen, setSmtpOpen] = usePersistedOpen("smtp", false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  // Communications
  const [commsOpen, setCommsOpen] = usePersistedOpen("comms", false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [commsSubject, setCommsSubject] = useState("");
  const [commsBody, setCommsBody] = useState("");
  const [commsRecipients, setCommsRecipients] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [commsHistory, setCommsHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = usePersistedOpen("comms_history", false);
  // User management
  const [resetEmail, setResetEmail] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  // Daily email
  const [dailyEmail, setDailyEmail] = useState({ enabled: false, send_hour_utc: 21, send_minute_utc: 0, template: "daily_performance" });
  const [dailySaving, setDailySaving] = useState(false);
  // Communications log
  const [commsLog, setCommsLog] = useState([]);
  const [logOpen, setLogOpen] = usePersistedOpen("comms_log", false);
  const [logFilter, setLogFilter] = useState("all");
  const [logSearch, setLogSearch] = useState("");
  // Collapsible panels
  const [backupOpen, setBackupOpen] = usePersistedOpen("backup", true);
  const [usersOpen, setUsersOpen] = usePersistedOpen("users", true);
  const [settingsOpen, setSettingsOpen] = usePersistedOpen("settings", false);
  const [dailyEmailOpen, setDailyEmailOpen] = usePersistedOpen("daily_email", true);
  const [backupSchedOpen, setBackupSchedOpen] = usePersistedOpen("backup_schedule", true);
  // Email backup schedule
  const [backupSched, setBackupSched] = useState({
    enabled: false, frequency: "daily", day_of_week: 1,
    send_hour_utc: 6, send_minute_utc: 0, recipient_email: "", last_sent: null,
  });
  const [backupSchedSaving, setBackupSchedSaving] = useState(false);
  const [backupSchedTesting, setBackupSchedTesting] = useState(false);

  useEffect(() => {
    Promise.all([
      api("/admin/stats").catch(() => ({ data: null })),
      api("/admin/users").catch(() => ({ data: [] })),
      apiGet("/settings/public").catch(() => ({ data: {} })),
      apiGet("/admin/smtp").catch(() => ({ data: null })),
      apiGet("/admin/communications/templates").catch(() => ({ data: [] })),
      apiGet("/admin/communications/history").catch(() => ({ data: [] })),
      apiGet("/admin/daily-email").catch(() => ({ data: { enabled: false, send_hour_utc: 21, send_minute_utc: 0 } })),
      apiGet("/admin/communications/log").catch(() => ({ data: [] })),
      apiGet("/admin/backup-schedule").catch(() => ({ data: null })),
    ]).then(([s, u, st, sm, tpl, hist, de, clog, bs]) => {
      setStats(s.data);
      setUsers(u.data || []);
      setCopyright(st.data?.copyright || "");
      setVersion(st.data?.version || "");
      if (sm.data) setSmtp(prev => ({ ...prev, ...sm.data }));
      setTemplates(tpl.data || []);
      setCommsHistory(hist.data || []);
      if (de.data) setDailyEmail(de.data);
      setCommsLog(clog.data || []);
      if (bs.data) setBackupSched(prev => ({ ...prev, ...bs.data }));
      setLoading(false);
    });
  }, []);

  const saveBackupSchedule = async () => {
    setBackupSchedSaving(true);
    try {
      const r = await apiPut(`/admin/backup-schedule`, backupSched);
      setBackupSched(prev => ({ ...prev, ...r.data }));
      toast.success("Backup schedule saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    }
    setBackupSchedSaving(false);
  };

  const testBackupEmail = async () => {
    if (!backupSched.recipient_email) {
      toast.error("Recipient email required");
      return;
    }
    setBackupSchedTesting(true);
    try {
      const r = await apiPost(`/admin/backup-schedule/test`, { recipient_email: backupSched.recipient_email });
      const sizeKB = ((r.data?.size_bytes || 0) / 1024).toFixed(1);
      toast.success(`Backup sent to ${r.data?.recipient} (${sizeKB} KB)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
    setBackupSchedTesting(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/export`, { withCredentials: true, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1]
        || `rockafellerjones_backup_${new Date().toISOString().slice(0, 10)}.json.gz`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${(res.data.size / 1e6).toFixed(2)} MB)`);
    } catch (e) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  const handleRestore = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.gz,application/json,application/gzip";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setRestoring(true);
      setRestoreResult(null);
      toast.loading(`Restoring ${file.name}… this can take a minute — keep this page open`, { id: "restore", duration: Infinity, description: undefined });
      try {
        const res = await axios.post(`${BACKEND}/api/admin/restore`, file, {
          withCredentials: true,
          headers: { "Content-Type": "application/octet-stream" },
          timeout: 600000,
        });
        let job = res.data;
        const dup = !!job.duplicate_ignored;
        while (job.status === "running") {
          const label = RESTORE_LABELS[`${job.stage}_replaced`] || job.stage || "starting";
          toast.loading(`Restoring ${file.name}…`, {
            id: "restore", duration: Infinity,
            description: job.stage_total ? `${label}: ${job.stage_done.toLocaleString()} / ${job.stage_total.toLocaleString()}` : `${label}…`,
          });
          await new Promise(r => setTimeout(r, 2000));
          for (let attempt = 1; ; attempt++) {
            try {
              job = (await axios.get(`${BACKEND}/api/admin/restore/${job.id}`, { withCredentials: true })).data;
              break;
            } catch (pollErr) {
              if (pollErr.response?.status === 404 || attempt >= 6) throw pollErr;
              await new Promise(r => setTimeout(r, 3000));
            }
          }
        }
        if (job.status === "failed") throw { response: { data: { detail: job.error } } };
        const data = { ...job, duplicate_ignored: dup };
        setRestoreResult({ ok: true, file: file.name, ...data });
        const warn = (data.warnings || []).length;
        toast[warn ? "warning" : "success"](warn ? "Restore finished — with warnings (see below)" : "Restore complete", {
          id: "restore", duration: 15000, description: `${file.name} · details shown in the Backup section`,
        });
        const s = await api("/admin/stats").catch(() => ({ data: null }));
        setStats(s.data);
      } catch (e) {
        const msg = e.response?.data?.detail || (e.code === "ECONNABORTED" ? "Timed out — the restore may still be finishing; refresh in a minute" : "Restore failed — check file format");
        setRestoreResult({ ok: false, file: file.name, error: msg });
        toast.error(msg, { id: "restore", duration: 15000, description: `${file.name} · details shown in the Backup section` });
      }
      setRestoring(false);
    };
    input.click();
  };

  if (!user || user.role !== "superadmin") {
    return (
      <div className="max-w-[600px] mx-auto px-4 md:px-8 py-20 text-center" data-testid="admin-denied">
        <ShieldCheck className="w-12 h-12 text-bt-text-tertiary mx-auto mb-4" />
        <p className="font-heading text-xl text-bt-text-primary mb-2">Access Denied</p>
        <p className="text-sm text-bt-text-secondary">Superadmin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" data-testid="admin-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <ShieldCheck className="w-6 h-6 text-bt-gold" strokeWidth={1.5} />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary">Admin</h1>
          <Badge className="bg-bt-gold/20 text-bt-gold border border-bt-gold/30 font-mono text-xs">SUPERADMIN</Badge>
        </div>
        <p className="text-base text-bt-text-secondary font-body mt-2">System management, data export & restore</p>
      </div>

      {/* System Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="admin-stats">
          {[
            { label: "Users", value: stats.users, icon: Users },
            { label: "Portfolios", value: stats.portfolios, icon: BarChart3 },
            { label: "Pageviews", value: stats.analytics, icon: Activity },
            { label: "Finnhub Live", value: `${stats.finnhub_live}/${stats.finnhub_total}`, icon: Activity },
            { label: "Watchlists", value: stats.watchlists, icon: Database },
            { label: "User Trades", value: stats.user_trades, icon: Activity },
            { label: "Woodrow Trades", value: stats.woodrow_trades, icon: Activity },
            { label: "Snapshots", value: stats.snapshots, icon: Database },
          ].map(s => (
            <div key={s.label} className="bg-bt-surface border border-bt-border-subtle px-3 py-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">{s.label}</p>
              <p className="text-lg font-mono font-medium text-bt-text-primary">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Analytics Dashboard */}
      <AnalyticsDashboard />

      {/* Export / Restore + Email Backup Schedule */}
      <div className="bg-bt-surface border border-bt-border-subtle mb-6" data-testid="admin-export-restore">
        <CollapsibleHeader
          title="Data Export & Restore"
          subtitle="Compressed (.json.gz) backup, restore, and automated email backups"
          icon={Database}
          open={backupOpen}
          onToggle={() => setBackupOpen(o => !o)}
          testid="toggle-backup"
        />
        {backupOpen && (
        <div className="px-6 pb-6">
        <Separator className="bg-bt-border-subtle mb-4" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export */}
          <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-bt-positive" strokeWidth={1.5} />
              <h3 className="font-body text-sm font-medium text-bt-text-primary">Export Data</h3>
            </div>
            <p className="text-xs text-bt-text-secondary mb-4">
              Downloads everything: users, portfolios, watchlists, trades, Woodrow accounts, trades and snapshots, settings, analytics, access logs and email history, as one compressed .json.gz file.
            </p>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="w-full bg-bt-positive text-bt-bg hover:bg-bt-positive/90 font-body"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Exporting..." : "Export All Data"}
            </Button>
          </div>

          {/* Restore */}
          <div className="bg-bt-surface-2 border border-bt-border-subtle p-4">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
              <h3 className="font-body text-sm font-medium text-bt-text-primary">Restore Data</h3>
            </div>
            <p className="text-xs text-bt-text-secondary mb-2">
              Upload a previously exported backup (.json.gz or older .json). This is a full replacement — existing data will be overwritten.
            </p>
            <div className="flex items-center gap-1 text-[10px] text-bt-negative mb-3">
              <AlertCircle className="w-3 h-3" /> Woodrow account will be overwritten if included in backup
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={restoring}
                  variant="outline"
                  className="w-full border-bt-gold text-bt-gold hover:bg-bt-gold/10 font-body"
                  data-testid="restore-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {restoring ? "Restoring..." : "Restore from Backup"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-bt-surface border-bt-border-subtle" data-testid="restore-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-bt-text-primary">Replace all data?</AlertDialogTitle>
                  <AlertDialogDescription className="text-bt-text-secondary">
                    Restoring deletes everything currently in the app (users, portfolios, Woodrow accounts and trades, settings, logs) and replaces it with the backup file you choose. This can't be undone. Consider exporting a backup first.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="restore-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRestore} className="bg-bt-negative text-white hover:bg-bt-negative/90" data-testid="restore-confirm-btn">
                    Choose backup file
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <RestoreResult result={restoreResult} onDismiss={() => setRestoreResult(null)} />
          </div>
        </div>

        {/* Automated Email Backup */}
        <div className="mt-5 bg-bt-surface-2 border border-bt-border-subtle" data-testid="admin-backup-schedule">
          <button
            onClick={() => setBackupSchedOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-bt-border-subtle hover:bg-bt-surface/40 transition-colors"
            data-testid="toggle-backup-schedule"
          >
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
              <h3 className="font-body text-sm font-medium text-bt-text-primary">Automated Email Backup</h3>
              {backupSched.enabled && (
                <Badge className="bg-bt-positive/15 text-bt-positive border-bt-positive/40 text-[9px] font-mono">ACTIVE</Badge>
              )}
              {backupSched.last_sent && (
                <span className="text-[10px] text-bt-text-tertiary font-mono">last: {new Date(backupSched.last_sent).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-bt-text-tertiary transition-transform ${backupSchedOpen ? "rotate-180" : ""}`} />
          </button>
          {backupSchedOpen && (
            <div className="p-4 space-y-3">
              <p className="text-xs text-bt-text-secondary">Email a compressed (.json.gz) snapshot of all data on a recurring schedule. Useful for off-site disaster-recovery copies.</p>
              {/* Enabled toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setBackupSched(s => ({ ...s, enabled: !s.enabled }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${backupSched.enabled ? "bg-bt-positive" : "bg-bt-border-subtle"}`}
                  data-testid="backup-enabled-toggle"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${backupSched.enabled ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <Label className="text-xs font-mono text-bt-text-secondary uppercase">Enabled</Label>
              </div>
              {/* Frequency */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Frequency</Label>
                  <div className="flex gap-2 mt-1">
                    {["daily", "weekly"].map(f => (
                      <button
                        key={f}
                        onClick={() => setBackupSched(s => ({ ...s, frequency: f }))}
                        className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${
                          backupSched.frequency === f
                            ? "bg-bt-gold/15 border-bt-gold/40 text-bt-gold"
                            : "bg-bt-surface border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-secondary"
                        }`}
                        data-testid={`backup-freq-${f}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                {backupSched.frequency === "weekly" && (
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Day of Week</Label>
                    <select
                      value={backupSched.day_of_week}
                      onChange={(e) => setBackupSched(s => ({ ...s, day_of_week: parseInt(e.target.value) }))}
                      className="mt-1 w-full bg-bt-surface border border-bt-border-subtle text-bt-text-primary px-3 py-2 text-xs font-mono"
                      data-testid="backup-day-of-week"
                    >
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {/* Time (local) */}
              {(() => {
                const localT = utcToLocal(backupSched.send_hour_utc, backupSched.send_minute_utc);
                const tz = getLocalTzLabel();
                const setLocal = (h, m) => {
                  const utc = localToUtc(h, m);
                  setBackupSched(s => ({ ...s, send_hour_utc: utc.hour, send_minute_utc: utc.minute }));
                };
                return (
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Send Time ({tz})</Label>
                    <TimeSelect
                      hour={localT.hour}
                      minute={localT.minute}
                      onChange={setLocal}
                      className="mt-1 w-full"
                      testid="backup-time"
                    />
                  </div>
                );
              })()}
              {/* Recipient email */}
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">Recipient Email</Label>
                <Input
                  type="email"
                  value={backupSched.recipient_email}
                  onChange={(e) => setBackupSched(s => ({ ...s, recipient_email: e.target.value }))}
                  placeholder="backups@example.com"
                  className="mt-1 bg-bt-surface border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="backup-recipient"
                />
              </div>
              {/* Save + Test */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={saveBackupSchedule}
                  disabled={backupSchedSaving}
                  className="flex-1 bg-bt-gold text-bt-bg hover:bg-bt-gold/90 font-body"
                  data-testid="backup-save"
                >
                  {backupSchedSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Schedule
                </Button>
                <Button
                  onClick={testBackupEmail}
                  disabled={backupSchedTesting || !backupSched.recipient_email}
                  variant="outline"
                  className="flex-1 border-bt-positive text-bt-positive hover:bg-bt-positive/10 font-body"
                  data-testid="backup-test"
                >
                  {backupSchedTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test Now
                </Button>
              </div>
            </div>
          )}
        </div>
        </div>
        )}
      </div>

      {/* Users */}
      <div className="bg-bt-surface border border-bt-border-subtle mb-6" data-testid="admin-users">
        <button
          onClick={() => setUsersOpen(o => !o)}
          className="w-full px-4 py-3 border-b border-bt-border-subtle flex items-center justify-between hover:bg-bt-surface-2/40 transition-colors"
          data-testid="toggle-users"
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
            <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">Registered Users ({users.length})</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-bt-text-tertiary transition-transform ${usersOpen ? "rotate-180" : ""}`} />
        </button>
        {usersOpen && (
        <>
        <div className="px-4 py-2 border-b border-bt-border-subtle flex justify-end">
          <AddUserDialog onCreated={(nu) => setUsers(prev => [...prev, nu])} />
        </div>
        <PendingAccessRequests onApproved={(nu) => setUsers(prev => [...prev, nu])} />
        <Table>
          <TableHeader>
            <TableRow className="border-bt-border-subtle hover:bg-transparent">
              <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-center">Notifications</TableHead>
              <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} className="border-bt-border-subtle hover:bg-bt-hover">
                <TableCell className="font-mono text-sm">
                  {u.role !== "superadmin" ? (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Emulate user ${u.email}? You will be logged in as this user.`)) return;
                        setActionLoading(u.email + "-emulate");
                        try {
                          startEmulation(user.email);
                          await apiPost(`/admin/emulate/${encodeURIComponent(u.email)}`);
                          toast.success(`Now viewing as ${u.email}`);
                          await checkAuth();
                          window.location.href = "/";
                        } catch (e) {
                          localStorage.removeItem("emulating_from");
                          toast.error(e.response?.data?.detail || "Emulation failed");
                        }
                        setActionLoading(null);
                      }}
                      disabled={actionLoading === u.email + "-emulate"}
                      className="text-bt-gold hover:text-bt-text-primary hover:underline transition-colors flex items-center gap-1.5"
                      data-testid={`emulate-${u.email}`}
                    >
                      <LogIn className="w-3 h-3" />
                      {u.email}
                    </button>
                  ) : (
                    <span className="text-bt-text-primary">{u.email}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-bt-text-secondary">{u.name || "—"}</TableCell>
                <TableCell>
                  <RoleSelect u={u} isSelf={u.email === user?.email}
                    onChanged={(role) => setUsers(prev => prev.map(x => x.email === u.email ? { ...x, role } : x))} />
                </TableCell>
                <TableCell>
                  {u.locked ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono text-bt-negative bg-bt-negative/10 border border-bt-negative/30">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  ) : u.awaiting_first_login ? (
                    <span title={u.welcome_sent_at ? `Welcome sent ${new Date(u.welcome_sent_at).toLocaleDateString()}` : "Hasn't logged in yet"}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono text-amber-600 bg-amber-500/10 border border-amber-500/40" data-testid={`user-status-invited-${u.email}`}>
                      <Mail className="w-3 h-3" /> Invited
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono text-bt-positive bg-bt-positive/10 border border-bt-positive/30" data-testid={`user-status-active-${u.email}`}>
                      <Unlock className="w-3 h-3" /> Active
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <button
                    onClick={async () => {
                      const newVal = !u.email_enabled;
                      try {
                        await apiPut(`/admin/users/${encodeURIComponent(u.email)}/email-toggle`, { email_enabled: newVal });
                        setUsers(prev => prev.map(x => x.email === u.email ? { ...x, email_enabled: newVal } : x));
                        toast.success(`${u.email}: emails ${newVal ? "enabled" : "disabled"}`);
                      } catch (e) { toast.error("Toggle failed"); }
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono transition-colors ${
                      u.email_enabled !== false
                        ? "text-bt-positive bg-bt-positive/10 border border-bt-positive/30 hover:bg-bt-positive/20"
                        : "text-bt-text-tertiary bg-bt-surface-2 border border-bt-border-subtle hover:bg-bt-hover"
                    }`}
                    data-testid={`email-toggle-${u.email}`}
                  >
                    <Mail className="w-3 h-3" />
                    {u.email_enabled !== false ? "On" : "Off"}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                  {u.role !== "superadmin" && (
                    <div className="flex items-center justify-end gap-1">
                      {/* Lock/Unlock */}
                      <Button
                        variant="ghost" size="sm"
                        onClick={async () => {
                          const action = u.locked ? "unlock" : "lock";
                          if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} account for ${u.email}?`)) return;
                          setActionLoading(u.email + "-lock");
                          try {
                            await apiPut(`/admin/users/${encodeURIComponent(u.email)}/lock`, { locked: !u.locked });
                            setUsers(prev => prev.map(x => x.email === u.email ? { ...x, locked: !u.locked } : x));
                            toast.success(`Account ${action}ed: ${u.email}`);
                          } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
                          setActionLoading(null);
                        }}
                        disabled={actionLoading === u.email + "-lock"}
                        className={`h-7 px-2 text-[10px] font-mono ${u.locked ? "text-bt-positive hover:bg-bt-positive/10" : "text-bt-negative hover:bg-bt-negative/10"}`}
                        data-testid={`lock-${u.email}`} title={u.locked ? "Unlock account" : "Lock account"}
                      >
                        {u.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                      </Button>
                      {/* Reset Password */}
                      {resetEmail === u.email ? (
                        <form className="flex items-center gap-1" onSubmit={async (e) => {
                          e.preventDefault();
                          if (!resetPassword || resetPassword.length < 4) { toast.error("Min 4 characters"); return; }
                          setActionLoading(u.email + "-reset");
                          try {
                            await apiPut(`/admin/users/${encodeURIComponent(u.email)}/reset-password`, { password: resetPassword });
                            toast.success(`Password reset for ${u.email}`);
                            setResetEmail(null);
                            setResetPassword("");
                          } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
                          setActionLoading(null);
                        }}>
                          <Input
                            type="password" placeholder="New password" value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)} autoFocus
                            className="h-7 w-28 text-xs bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                          />
                          <Button type="submit" variant="ghost" size="sm" disabled={actionLoading === u.email + "-reset"}
                            className="h-7 px-2 text-bt-positive hover:bg-bt-positive/10">
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => { setResetEmail(null); setResetPassword(""); }}
                            className="h-7 px-1 text-bt-text-tertiary hover:bg-bt-surface-2">
                            <AlertCircle className="w-3 h-3" />
                          </Button>
                        </form>
                      ) : (
                        <Button variant="ghost" size="sm"
                          onClick={() => { setResetEmail(u.email); setResetPassword(""); }}
                          className="h-7 px-2 text-[10px] font-mono text-bt-text-secondary hover:bg-bt-surface-2"
                          data-testid={`reset-pw-${u.email}`} title="Reset password"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                  {u.role !== "superadmin" && (
                    <EmailUserDialog u={u} onSent={(kind, at) => setUsers(prev => prev.map(x => x.email === u.email ? { ...x, last_email_sent_at: at, last_email_kind: kind, ...(kind !== "winback" ? { welcome_sent_at: at } : {}) } : x))} />
                  )}
                  {u.email !== user?.email && (
                    <DeleteUserButton u={u} onDeleted={() => setUsers(prev => prev.filter(x => x.email !== u.email))} />
                  )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </>
        )}
      </div>

      <AdminAuditLog refreshKey={users} />

      <StockUniversePanel />

      <DataCleanupPanel />

      {/* App Settings */}
      <div className="bg-bt-surface border border-bt-border-subtle mt-6 mb-6" data-testid="admin-settings">
        <CollapsibleHeader
          title="App Settings"
          subtitle="Copyright notice shown on the login page · version updates automatically"
          icon={Settings}
          open={settingsOpen}
          onToggle={() => setSettingsOpen(o => !o)}
          testid="toggle-settings"
        />
        {settingsOpen && (
        <div className="px-6 pb-6">
        <Separator className="bg-bt-border-subtle mb-4" />
        <div className="space-y-4">
          <div>
            <Label className="text-bt-text-primary font-body text-sm font-medium">Copyright Text</Label>
            <Input
              value={copyright}
              onChange={(e) => setCopyright(e.target.value)}
              placeholder="2025/2026 - Rockafeller Jones. All rights reserved."
              className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
              data-testid="setting-copyright-input"
            />
          </div>
          <div>
            <Label className="text-bt-text-primary font-body text-sm font-medium">Version Number</Label>
            <Input
              value={version}
              readOnly
              className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-tertiary font-mono w-40 cursor-not-allowed"
              data-testid="setting-version-input"
            />
            <p className="mt-1 font-body text-[11px] text-bt-text-tertiary" data-testid="setting-version-note">
              Updated automatically with each release. Click the version in the footer to see release notes.
            </p>
          </div>
          <Button
            onClick={async () => {
              setSavingSettings(true);
              try {
                await apiPut("/admin/settings", { copyright });
                toast.success("Settings saved");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to save");
              }
              setSavingSettings(false);
            }}
            disabled={savingSettings}
            size="sm"
            className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body text-xs"
            data-testid="save-settings-btn"
          >
            <Check className="w-3 h-3 mr-1" /> {savingSettings ? "Saving..." : "Save Settings"}
          </Button>
        </div>
        </div>
        )}
      </div>

      {/* SMTP Settings */}
      <div className="bg-bt-surface border border-bt-border-subtle mt-6" data-testid="admin-smtp">
        <button
          onClick={() => setSmtpOpen(!smtpOpen)}
          className="w-full flex items-center gap-3 px-6 py-4 hover:bg-bt-hover transition-colors text-left"
          data-testid="toggle-smtp"
        >
          <Mail className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <div className="flex-1">
            <h2 className="font-heading text-xl font-medium text-bt-text-primary">SMTP Configuration</h2>
            <p className="text-sm text-bt-text-secondary font-body">Configure email server for sending communications</p>
          </div>
          {smtp.configured && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-bt-positive/10 border border-bt-positive/30 text-bt-positive text-[10px] font-mono">
              <Check className="w-3 h-3" /> Configured
            </span>
          )}
          <ChevronDown className={`w-5 h-5 text-bt-text-tertiary shrink-0 transition-transform ${smtpOpen ? "rotate-180" : ""}`} />
        </button>
        {smtpOpen && (
          <div className="px-6 pb-6 space-y-4">
            <Separator className="bg-bt-border-subtle" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">SMTP Host</Label>
                <Input
                  value={smtp.host}
                  onChange={(e) => setSmtp(s => ({ ...s, host: e.target.value }))}
                  placeholder="smtp.gmail.com"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="smtp-host-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">Port</Label>
                <Input
                  type="number"
                  value={smtp.port}
                  onChange={(e) => setSmtp(s => ({ ...s, port: parseInt(e.target.value) || 587 }))}
                  placeholder="587"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono w-28"
                  data-testid="smtp-port-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">Username</Label>
                <Input
                  value={smtp.username}
                  onChange={(e) => setSmtp(s => ({ ...s, username: e.target.value }))}
                  placeholder="your@email.com"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="smtp-username-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">Password</Label>
                <Input
                  type="password"
                  value={smtp.password}
                  onChange={(e) => setSmtp(s => ({ ...s, password: e.target.value }))}
                  placeholder="App password or SMTP key"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="smtp-password-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">From Email</Label>
                <Input
                  value={smtp.from_email}
                  onChange={(e) => setSmtp(s => ({ ...s, from_email: e.target.value }))}
                  placeholder="noreply@rockafellerjones.com"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="smtp-from-email-input"
                />
              </div>
              <div>
                <Label className="text-bt-text-primary font-body text-sm font-medium">From Name</Label>
                <Input
                  value={smtp.from_name}
                  onChange={(e) => setSmtp(s => ({ ...s, from_name: e.target.value }))}
                  placeholder="Rockafeller Jones"
                  className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                  data-testid="smtp-from-name-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={smtp.use_tls}
                onChange={(e) => setSmtp(s => ({ ...s, use_tls: e.target.checked }))}
                className="accent-bt-gold"
                id="smtp-tls"
                data-testid="smtp-tls-checkbox"
              />
              <Label htmlFor="smtp-tls" className="text-bt-text-secondary font-body text-sm cursor-pointer">Use TLS (recommended)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={async () => {
                  setSavingSmtp(true);
                  try {
                    await apiPut("/admin/smtp", smtp);
                    toast.success("SMTP settings saved");
                    const res = await apiGet("/admin/smtp").catch(() => ({ data: null }));
                    if (res.data) setSmtp(prev => ({ ...prev, ...res.data }));
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to save");
                  }
                  setSavingSmtp(false);
                }}
                disabled={savingSmtp}
                size="sm"
                className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body text-xs"
                data-testid="save-smtp-btn"
              >
                <Check className="w-3 h-3 mr-1" /> {savingSmtp ? "Saving..." : "Save SMTP"}
              </Button>
              <Button
                onClick={async () => {
                  setTestingSmtp(true);
                  try {
                    const res = await apiPost("/admin/smtp/test", { to: smtp.username || smtp.from_email });
                    toast.success(`Test email sent to ${res.data.to}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Test failed");
                  }
                  setTestingSmtp(false);
                }}
                disabled={testingSmtp || !smtp.host}
                variant="outline"
                size="sm"
                className="border-bt-border-focus text-bt-text-secondary hover:text-bt-text-primary font-body text-xs"
                data-testid="test-smtp-btn"
              >
                <Send className="w-3 h-3 mr-1" /> {testingSmtp ? "Sending..." : "Send Test Email"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Communications */}
      <div className="bg-bt-surface border border-bt-border-subtle mt-6" data-testid="admin-communications">
        <button
          onClick={() => setCommsOpen(!commsOpen)}
          className="w-full flex items-center gap-3 px-6 py-4 hover:bg-bt-hover transition-colors text-left"
          data-testid="toggle-communications"
        >
          <Send className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <div className="flex-1">
            <h2 className="font-heading text-xl font-medium text-bt-text-primary">Communications</h2>
            <p className="text-sm text-bt-text-secondary font-body">Send emails to users using templates or custom messages</p>
          </div>
          <ChevronDown className={`w-5 h-5 text-bt-text-tertiary shrink-0 transition-transform ${commsOpen ? "rotate-180" : ""}`} />
        </button>
        {commsOpen && (
          <div className="px-6 pb-6 space-y-4">
            <Separator className="bg-bt-border-subtle" />

            {!smtp.configured && !smtp.host && (
              <div className="flex items-center gap-2 px-3 py-2 bg-bt-negative/5 border border-bt-negative/20 text-bt-negative text-xs font-mono" data-testid="comms-smtp-warning">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> Configure SMTP settings above before sending emails
              </div>
            )}

            {/* Template Selector */}
            <div>
              <Label className="text-bt-text-primary font-body text-sm font-medium">Template</Label>
              <select
                value={selectedTemplate}
                onChange={(e) => {
                  const tpl = templates.find(t => t.id === e.target.value);
                  setSelectedTemplate(e.target.value);
                  if (tpl) {
                    setCommsSubject(tpl.subject);
                    setCommsBody(tpl.body);
                  }
                }}
                className="mt-1 w-full bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary font-mono text-sm px-3 py-2 focus:outline-none focus:border-bt-gold"
                data-testid="comms-template-select"
              >
                <option value="">Select a template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Recipients */}
            <div>
              <Label className="text-bt-text-primary font-body text-sm font-medium">Recipients</Label>
              <div className="flex items-center gap-3 mt-1">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="recipients"
                    value="all"
                    checked={commsRecipients === "all"}
                    onChange={() => setCommsRecipients("all")}
                    className="accent-bt-gold"
                    data-testid="comms-recipients-all"
                  />
                  <span className="text-sm font-mono text-bt-text-secondary">All Users ({users.length})</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="recipients"
                    value="select"
                    checked={commsRecipients === "select"}
                    onChange={() => setCommsRecipients("select")}
                    className="accent-bt-gold"
                    data-testid="comms-recipients-select"
                  />
                  <span className="text-sm font-mono text-bt-text-secondary">Select Users</span>
                </label>
              </div>
              {commsRecipients === "select" && (
                <div className="mt-2 max-h-[120px] overflow-y-auto bg-bt-surface-2 border border-bt-border-subtle p-2 space-y-1">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1 hover:bg-bt-hover cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(u.email)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedUsers(s => [...s, u.email]);
                          else setSelectedUsers(s => s.filter(x => x !== u.email));
                        }}
                        className="accent-bt-gold"
                        data-testid={`comms-user-${u.email}`}
                      />
                      <span className="font-mono text-xs text-bt-text-primary">{u.email}</span>
                      <span className="text-[10px] text-bt-text-tertiary ml-auto">{u.name || ""}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <Label className="text-bt-text-primary font-body text-sm font-medium">Subject</Label>
              <Input
                value={commsSubject}
                onChange={(e) => setCommsSubject(e.target.value)}
                placeholder="Email subject line"
                className="mt-1 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono"
                data-testid="comms-subject-input"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="text-bt-text-primary font-body text-sm font-medium">
                Message Body <span className="text-bt-text-tertiary font-normal">(HTML supported)</span>
              </Label>
              <textarea
                value={commsBody}
                onChange={(e) => setCommsBody(e.target.value)}
                placeholder="Compose your message... HTML tags are supported."
                rows={8}
                className="mt-1 w-full bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary font-mono text-sm px-3 py-2 focus:outline-none focus:border-bt-gold resize-y"
                data-testid="comms-body-input"
              />
            </div>

            {/* Send Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={async () => {
                  setSending(true);
                  try {
                    const recipients = commsRecipients === "all" ? ["all"] : selectedUsers;
                    const res = await apiPost("/admin/communications/send", {
                      recipients,
                      subject: commsSubject,
                      body: commsBody,
                      template_id: selectedTemplate,
                    });
                    toast.success(`Sent to ${res.data.sent} recipient${res.data.sent !== 1 ? "s" : ""}${res.data.failed > 0 ? `, ${res.data.failed} failed` : ""}`);
                    // Refresh history + log
                    const hist = await apiGet("/admin/communications/history").catch(() => ({ data: [] }));
                    setCommsHistory(hist.data || []);
                    const clog = await apiGet("/admin/communications/log").catch(() => ({ data: [] }));
                    setCommsLog(clog.data || []);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Send failed");
                  }
                  setSending(false);
                }}
                disabled={sending || !commsSubject || !commsBody}
                className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body text-xs"
                data-testid="comms-send-btn"
              >
                {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                {sending ? "Sending..." : "Send Email"}
              </Button>
              <Button
                onClick={() => { setCommsSubject(""); setCommsBody(""); setSelectedTemplate(""); setSelectedUsers([]); }}
                variant="outline"
                size="sm"
                className="border-bt-border-focus text-bt-text-secondary hover:text-bt-text-primary font-body text-xs"
                data-testid="comms-clear-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>

            {/* History */}
            {commsHistory.length > 0 && (
              <div className="mt-2">
                <button
                  onClick={() => setHistoryOpen(!historyOpen)}
                  className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-bt-text-secondary hover:text-bt-text-primary transition-colors"
                  data-testid="toggle-comms-history"
                >
                  <Clock className="w-3.5 h-3.5 text-bt-gold" />
                  Sent History ({commsHistory.length})
                  <ChevronDown className={`w-3.5 h-3.5 text-bt-text-tertiary shrink-0 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
                </button>
                {historyOpen && (
                  <div className="mt-2 space-y-1">
                    {commsHistory.map((h, i) => (
                      <div key={i} className="flex items-center justify-between bg-bt-surface-2 border border-bt-border-subtle px-3 py-2" data-testid={`comms-history-${i}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-xs text-bt-text-primary truncate">{h.subject}</p>
                          <p className="text-[10px] text-bt-text-tertiary font-mono">
                            To: {h.recipients?.length > 2 ? `${h.recipients.length} recipients` : h.recipients?.join(", ")}
                          </p>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className="text-[10px] font-mono text-bt-positive">{h.sent_count} sent</p>
                          <p className="text-[10px] font-mono text-bt-text-tertiary">
                            {h.timestamp ? new Date(h.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Automated Daily Email */}
      <div className="bg-bt-surface border border-bt-border-subtle p-6" data-testid="daily-email-section">
        <button
          onClick={() => setDailyEmailOpen(o => !o)}
          className={`w-full flex items-center gap-3 text-left ${dailyEmailOpen ? "mb-4" : ""}`}
          data-testid="toggle-daily-email"
        >
          <Clock className="w-5 h-5 text-bt-gold" />
          <h2 className="font-heading text-xl font-medium text-bt-text-primary">Automated Daily Email</h2>
          <span className={`ml-auto inline-flex px-2 py-0.5 text-xs font-mono ${dailyEmail.enabled ? "bg-bt-positive/15 text-bt-positive border border-bt-positive/30" : "bg-bt-surface-2 text-bt-text-tertiary border border-bt-border-subtle"}`}>
            {dailyEmail.enabled ? "Active" : "Disabled"}
          </span>
          <ChevronDown className={`w-5 h-5 text-bt-text-tertiary shrink-0 transition-transform ${dailyEmailOpen ? "rotate-180" : ""}`} />
        </button>
        {dailyEmailOpen && (<>
        <p className="text-sm text-bt-text-secondary mb-4">
          Sends a personalized daily performance report to all users every market day (Mon–Fri).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Status</label>
            <select
              value={dailyEmail.enabled ? "enabled" : "disabled"}
              onChange={(e) => setDailyEmail(prev => ({ ...prev, enabled: e.target.value === "enabled" }))}
              className="w-full bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary font-mono text-sm px-3 py-2"
              data-testid="daily-email-toggle"
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div>
            {(() => {
              const localT = utcToLocal(dailyEmail.send_hour_utc, dailyEmail.send_minute_utc);
              const tz = getLocalTzLabel();
              const setLocal = (h, m) => {
                const utc = localToUtc(h, m);
                setDailyEmail(prev => ({ ...prev, send_hour_utc: utc.hour, send_minute_utc: utc.minute }));
              };
              return (
                <>
                  <label className="block text-xs font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Send Time ({tz})</label>
                  <TimeSelect
                    hour={localT.hour}
                    minute={localT.minute}
                    onChange={setLocal}
                    className="w-full"
                    testid="daily-email-time"
                  />
                  <p className="text-[10px] text-bt-text-tertiary mt-1 font-mono" data-testid="daily-email-time-hint">
                    = {utcToEt(dailyEmail.send_hour_utc, dailyEmail.send_minute_utc)} ET · {formatTime12(dailyEmail.send_hour_utc, dailyEmail.send_minute_utc)} UTC
                  </p>
                </>
              );
            })()}
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-bt-text-tertiary mb-1">Last Sent</label>
            <p className="font-mono text-sm text-bt-text-secondary py-2">
              {dailyEmail.last_sent ? new Date(dailyEmail.last_sent).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "Never"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={async () => {
              setDailySaving(true);
              try {
                await apiPut("/admin/daily-email", dailyEmail);
                toast.success(dailyEmail.enabled ? "Daily emails enabled" : "Daily emails disabled");
              } catch (e) { toast.error("Failed to save"); }
              setDailySaving(false);
            }}
            disabled={dailySaving}
            className="bg-bt-gold text-bt-bg hover:bg-bt-gold/80 font-mono text-xs px-4 py-2"
            data-testid="save-daily-email"
          >
            {dailySaving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Check className="w-3 h-3 mr-2" />}
            Save Settings
          </Button>
          <Button
            onClick={async () => {
              if (!window.confirm("Send daily performance report to all users now?")) return;
              setDailySaving(true);
              try {
                const tpl = templates.find(t => t.id === "daily_performance");
                const res = await apiPost("/admin/communications/send", {
                  recipients: ["all"],
                  subject: tpl?.subject || "Daily Woodrow Report — Rockafeller Jones",
                  body: tpl?.body || "",
                  template_id: "daily_performance",
                });
                toast.success(`Daily report sent to ${res.data.sent} user${res.data.sent !== 1 ? "s" : ""}`);
                const clog = await apiGet("/admin/communications/log").catch(() => ({ data: [] }));
                setCommsLog(clog.data || []);
              } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
              setDailySaving(false);
            }}
            disabled={dailySaving}
            variant="outline"
            className="border-bt-gold/40 text-bt-gold hover:bg-bt-gold/10 font-mono text-xs px-4 py-2"
            data-testid="send-daily-now"
          >
            <Send className="w-3 h-3 mr-2" />
            Send Now
          </Button>
        </div>
        </>)}
      </div>

      {/* Communications Log */}
      <div className="bg-bt-surface border border-bt-border-subtle p-6" data-testid="comms-log-section">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setLogOpen(!logOpen)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setLogOpen(!logOpen); }}
          className="flex items-center gap-3 w-full text-left cursor-pointer"
          data-testid="toggle-comms-log"
        >
          <FileText className="w-5 h-5 text-bt-gold" />
          <h2 className="font-heading text-xl font-medium text-bt-text-primary">Communications Log</h2>
          <span className="ml-2 text-xs font-mono text-bt-text-tertiary">({commsLog.length} entries)</span>
          <Button
            variant="ghost" size="sm"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const res = await apiGet("/admin/communications/log");
                setCommsLog(res.data || []);
                toast.success("Log refreshed");
              } catch { toast.error("Failed to refresh"); }
            }}
            className="ml-auto h-7 px-2 text-bt-text-tertiary hover:text-bt-text-primary"
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
          <ChevronDown className={`w-5 h-5 text-bt-text-tertiary shrink-0 transition-transform ${logOpen ? "rotate-180" : ""}`} />
        </div>
        {logOpen && (
          <div className="mt-4">
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="comms-log-filters">
              {LOG_FILTERS.map(f => {
                const n = commsLog.filter(f.match).length;
                return (
                  <button key={f.id} onClick={() => setLogFilter(f.id)} data-testid={`comms-filter-${f.id}`}
                    className={`px-2.5 py-1 text-[11px] font-mono border transition-colors ${logFilter === f.id ? "bg-bt-gold/15 border-bt-gold/50 text-bt-gold" : "border-bt-border-subtle text-bt-text-secondary hover:border-bt-border-focus"}`}>
                    {f.label} <span className="opacity-60">{n}</span>
                  </button>
                );
              })}
              <Input value={logSearch} onChange={(e) => setLogSearch(e.target.value)} placeholder="Search recipient or subject..."
                className="ml-auto h-8 w-full sm:w-64 bg-bt-surface-2 border-bt-border-subtle font-mono text-xs" data-testid="comms-log-search" />
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-bt-border-subtle hover:bg-transparent">
                  <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Time</TableHead>
                  <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Recipient</TableHead>
                  <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Subject</TableHead>
                  <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Source</TableHead>
                  <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const f = LOG_FILTERS.find(x => x.id === logFilter) || LOG_FILTERS[0];
                  const q = logSearch.trim().toLowerCase();
                  const rows = commsLog.filter(e => f.match(e) && (!q || `${e.recipient} ${e.subject}`.toLowerCase().includes(q)));
                  return rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-bt-text-tertiary font-mono text-sm py-8" data-testid="comms-log-empty">{commsLog.length ? "No emails match these filters" : "No emails sent yet"}</TableCell></TableRow>
                ) : rows.map((entry, i) => (
                  <TableRow key={i} className="border-bt-border-subtle hover:bg-bt-hover">
                    <TableCell className="font-mono text-xs text-bt-text-tertiary whitespace-nowrap">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-bt-text-primary">{entry.recipient}</TableCell>
                    <TableCell className="font-mono text-xs text-bt-text-secondary max-w-[200px] truncate">{entry.subject}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-mono ${
                        entry.source === "manual"
                          ? "bg-bt-surface-2 text-bt-text-secondary border border-bt-border-subtle"
                          : "bg-blue-500/15 text-blue-400 border border-blue-500/30"
                      }`} data-testid="comm-log-source">
                        {SOURCE_LABELS[entry.source] || (entry.source || "manual").replace(/_/g, " ")}
                      </span>
                    </TableCell>
                    <TableCell>
                      {entry.status === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-bt-positive">
                          <Check className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-bt-negative" title={entry.error || ""}>
                          <AlertCircle className="w-3 h-3" /> Failed
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ));
                })()}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
